import React, { useState, useEffect } from 'react';
import { Grid, Typography, Button, IconButton, Divider } from '@mui/material';
import { Add, Edit } from '@mui/icons-material';
import UserSelectModal from './UserSearchModal';
import DateFilter from './DateFilter';
import PositionSelector, { Position } from './PositionSelector';

interface SaveData {
  id?: string;
  positionId?: string;
  committeeId?: string;
  action: 'newAppointment' | 'editDates' | 'removeRecord';
  userId?: string;
  startDate?: string | null;
  endDate?: string | null;
}

interface AddPositionProps {
  buttonText: string;
  positions?: Position[];
  onAdd: (appointment: SaveData) => void;
  committeeId: string;
  isSaving: boolean;
  savingError?: string | null;
  saveSuccess?: string | boolean | null;
  spdChapterId?: string | null;
  localChapterId?: string | null;
  onLoadPositions?: (committeeId: string) => Promise<void>;
  isLoadingPositions?: boolean;
}

interface UserObject {
  id: string;
  full_name: string;
}

const AddPosition: React.FC<AddPositionProps> = ({
  buttonText,
  positions = [],
  onAdd,
  committeeId,
  isSaving,
  savingError,
  saveSuccess,
  spdChapterId,
  localChapterId,
  onLoadPositions,
  isLoadingPositions = false,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [sentToSave, setSentToSave] = useState<boolean>(false);
  const [positionSelected, setPositionSelected] = useState<string | null>(null);
  const [showUserSelect, setShowUserSelect] = useState<boolean>(false);
  const [userSelected, setUserSelected] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [addingPosition, setAddingPosition] = useState<boolean>(false);
  const [userName, setUserName] = useState<string | null>(null);

  const resetState = () => {
    setError(null);
    setSentToSave(false);
    setPositionSelected(null);
    setShowUserSelect(false);
    setUserSelected(null);
    setStartDate(null);
    setAddingPosition(false);
    setUserName(null);
  };

  useEffect(() => {
    if (savingError) {
      setError(savingError);
      setSentToSave(false);
    }
    if (isSaving) {
      setError(null);
    }
  }, [isSaving, savingError]);

  useEffect(() => {
    if (sentToSave && !isSaving && !savingError) {
      resetState();
    }
  }, [isSaving, savingError, sentToSave]);

  const handleAddPosition = async () => {
    setAddingPosition(true);

    // Load positions if onLoadPositions callback is provided
    if (onLoadPositions) {
      await onLoadPositions(committeeId);
    }

    setShowUserSelect(true);
    if (positions.length === 1) {
      setPositionSelected(positions[0].id);
    }
  };

  const handleUserSelect = (user: UserObject) => {
    const userId = user.id;
    const userName = user.full_name;
    setUserSelected(userId);
    setUserName(userName);
    setShowUserSelect(false);
  };

  const handleSave = () => {
    // validate then call function passed in
    if (!userSelected) {
      setError('Please select a user');
      return;
    }
    if (!positionSelected) {
      setError('Please select a position');
      return;
    }
    if (!startDate) {
      setError('Please select a start date');
      return;
    }
    setError(null);
    setSentToSave(true);
    onAdd({
      action: 'newAppointment',
      userId: userSelected,
      positionId: positionSelected,
      committeeId: committeeId,
      startDate: startDate,
    });
  };

  const handlePositionChange = (selectedId: string | string[] | null) => {
    setPositionSelected(selectedId as string);
  };

  return (
    <Grid container>
      {!addingPosition ? (
        <Grid item xs={12} textAlign={'center'}>
          <Button
            variant="contained"
            color="success"
            onClick={() => handleAddPosition()}
            size="small"
            disabled={isSaving || isLoadingPositions}
            sx={{
              fontWeight: 'bold',
            }}
          >
            <Add sx={{ fontSize: 17, pr: 1 }} />
            {isLoadingPositions ? 'Loading Positions...' : buttonText}
          </Button>
        </Grid>
      ) : (
        <Grid container item xs={12}>
          <Grid item xs={12}>
            <Divider sx={{ marginBottom: 2 }} />
            <Typography variant="body1">
              {userName ? userName : 'Please select a user'}
              <IconButton
                onClick={() => {
                  setShowUserSelect(true);
                }}
                sx={{ padding: 0, marginLeft: 1 }}
              >
                <Edit fontSize="small" color="primary" sx={{ fontSize: 18 }} />
              </IconButton>
            </Typography>
          </Grid>
          <Grid item xs={12} sx={{ paddingTop: 1, paddingBottom: 1 }}>
            <PositionSelector
              value={positionSelected}
              onChange={handlePositionChange}
              positions={positions}
              multiple={false}
              label="Position"
              variant="standard"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sx={{ paddingTop: 1, paddingBottom: 1 }}>
            <DateFilter
              filterValue={startDate}
              updateFilters={setStartDate}
              label={'Start Date'}
              disabled={false}
            />
          </Grid>
          <Grid item xs={12} sx={{ paddingBottom: 1 }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => {
                handleSave();
              }}
              disabled={sentToSave}
              sx={{ fontWeight: 'bold' }}
            >
              Save
            </Button>
            <Button
              variant="contained"
              color="warning"
              size="small"
              onClick={() => {
                resetState();
              }}
              disabled={sentToSave}
              sx={{ fontWeight: 'bold', marginLeft: 1 }}
            >
              Cancel
            </Button>
          </Grid>
          {error && (
            <Grid item xs={12} sx={{ paddingBottom: 1 }}>
              <Typography variant="body2" color="error">
                {error}
              </Typography>
            </Grid>
          )}
        </Grid>
      )}

      <UserSelectModal
        modalOpen={showUserSelect}
        setModalOpen={setShowUserSelect}
        setUser={handleUserSelect}
        allowSetToMe={false}
        userObjectRet={true}
        chapter={localChapterId}
        spd={spdChapterId}
        statusFilter="active"
      />
    </Grid>
  );
};

export default AddPosition;
